import type { ComponentProps, ElementType } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { TextContainerPosition } from 'graphql-ts-types';
import type {
  AlignSelfProps,
  BorderProps,
  BorderRadiusProps,
  ColorProps,
  DisplayProps,
  OpacityProps,
  PositionProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  alignSelf,
  border,
  borderRadius,
  color,
  display,
  position,
  space,
  width,
  zIndex,
} from 'styled-system';

import BaseLegalDisclaimer from 'common/components/LegalDisclaimer';
import { LEGAL_DISCLAIMER_BANNER_Z_INDEX } from 'common/constants/z-index';
import { useMatchesViewport } from 'common/hooks/media';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import type {
  ThemeBottomProps,
  ThemeLeftProps,
  ThemeRightProps,
} from 'common/theme/system-utilities';
import { themeBottom, themeLeft, themeRight } from 'common/theme/system-utilities';
import type { BannerProps } from '../../../types';

interface LegalDisclaimerProps extends DisplayProps, SpaceProps {
  isStaticBackground?: boolean;
  legalDisclaimerContent?: BannerProps['legalDisclaimerContent'];
  textContainerPosition?: BannerProps['textContainerPosition'];
  isFullWidth?: BannerProps['isFullWidth'];
}

const InnerLegalDisclaimer = styled(BaseLegalDisclaimer)<
  SpaceProps &
    WidthProps &
    DisplayProps &
    ColorProps &
    BorderProps &
    PositionProps &
    ZIndexProps &
    ThemeBottomProps &
    ThemeRightProps &
    ThemeLeftProps &
    AlignSelfProps &
    BorderRadiusProps &
    OpacityProps
>`
  ${display};
  ${width};
  ${space};
  ${color};
  ${position};
  ${border};
  ${zIndex};
  ${themeBottom};
  ${themeRight};
  ${themeLeft};
  ${alignSelf};
  ${borderRadius};

  // setting opacity without affecting the text color
  background-color: rgba(255, 255, 255, 0.6);
`;

const innerLegalDisclaimerStyles: Partial<
  ComponentProps<typeof InnerLegalDisclaimer> & { as: ElementType }
> = {
  p: 'sp_4',
  bg: 'white',
  position: 'absolute',
  border: 'none',
  borderRadius: 'medium',
  zIndex: LEGAL_DISCLAIMER_BANNER_Z_INDEX,
  themeBottom: ['sp_8', null, null, 'sp_12'],
};

const LegalDisclaimer = ({
  isStaticBackground,
  legalDisclaimerContent,
  textContainerPosition,
  isFullWidth,
  ...styleProps
}: LegalDisclaimerProps) => {
  const { t } = useLocalization();
  const {
    matches: { matchesDesktop },
  } = useMatchesViewport();
  if (!legalDisclaimerContent) return null;

  const sharedProps = {
    ...styleProps,
    ...innerLegalDisclaimerStyles,
    label: `*${t('h24_more_info')}`,
    color: 'sys.neutral.text.strong' as const,
    content: legalDisclaimerContent,
    ...(isStaticBackground ? { position: 'static' as const, width: 'fit-content' } : {}),
  };

  if (isFullWidth && textContainerPosition === TextContainerPosition.Right && matchesDesktop) {
    // On mobile/tablet: position always on the right
    // On desktop: position on the left
    return <InnerLegalDisclaimer {...sharedProps} themeLeft={[null, null, 'sp_8', 'sp_12']} />;
  } else {
    return <InnerLegalDisclaimer {...sharedProps} themeRight={['sp_8', null, null, 'sp_12']} />;
  }
};

export default LegalDisclaimer;
